import { useEntitlements } from "src/hooks/use-entitlement";

/**
 * Whether realtime audiences are available to the workspace.
 *
 * The entitlement subsumes LaunchDarkly — the backend config is
 * `enable-realtime-audiences` OR `enable-realtime-audiences-setup`, evaluated
 * with the caller's own LD context — and is the only source that sees an
 * Entitlement Center grant. Call sites wanting only the setup flag read it
 * directly.
 *
 * Suspends, because `realtimeAudiences` has no synchronous source and
 * `RealtimeSyncWrapper` redirects on `false`. So call it from route content
 * only: `<Nav />` renders outside the layout's `<Suspense>` boundary and has
 * nothing to suspend into.
 */
export const useRealtimeAudiencesEnabled = (): boolean => {
  const { data } = useEntitlements(true, true);

  return Boolean(data.entitlements.realtimeAudiences);
};

/** The same answer without suspending, for UI outside the layout's `<Suspense>`. `enabled` is false until entitlements load. */
export const useRealtimeAudiencesEnabledWithoutSuspense = (): {
  enabled: boolean;
  isLoading: boolean;
} => {
  const { data, isLoading } = useEntitlements(true);

  return {
    enabled: Boolean(data.entitlements.realtimeAudiences),
    isLoading,
  };
};
